import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where, doc, setDoc, deleteDoc, addDoc, serverTimestamp, orderBy, limit } from "firebase/firestore";
import { onSnapshot } from "../utils/firestoreListen";
import { db } from "../firebase";
import { useNavigate, useLocation } from "react-router-dom";
import ArtworkReactions from "../components/ArtworkReactions";
import KidBackground from '../ui/backgrounds/KidBackground';
import DailyActivitiesShowcase from "../components/DailyActivitiesShowcase";
import BookLearningSpace from "../components/BookLearningSpace";
import ArtHistoryCoLearningSpace from "../components/ArtHistoryCoLearningSpace";
import { useAuth } from "../context/AuthContext";
import { getCache, setCache, createCacheKey } from "../utils/cache";
import { backendConfig } from "../config/backendConfig";
import { resolveImageUrl, warmResolvedImageUrls } from "../utils/urlResolver";
import SmartImage from "../components/SmartImage";
import {
  coLearningExpandedGalleryViewerBackdropProps,
  coLearningGalleryPickerBackdropProps,
  coLearningGallerySheetBackdropProps,
  coLearningGalleryUnshareBackdropProps,
  coLearningGalleryUploadBackdropProps,
  coLearningInkBody,
  coLearningInkHeading,
  coLearningInkMuted,
  coLearningInkTitle,
  coLearningInkLink,
} from '../ui/coLearningModalTheme';
import { LayoutDashboard, User, Home, ChevronDown } from 'lucide-react';
import splashImage from '../assets/splash.png';
import { ArtistSalonAgeFilterProvider, useArtistSalonAgeFilter } from '../context/ArtistSalonAgeFilterContext';
import ArtistAgeFilterSelect, { SALON_FILTER_SELECT_CLASS } from '../components/ArtistAgeFilterSelect';
import { artistMatchesAgeFilter } from '../utils/artistAge';
import { artNavPillBtn, artNavCtaBtn } from '../ui/artmosphereButtonTheme';

const pillNavBtn = artNavPillBtn;
const joinNavBtn = artNavCtaBtn;

const artmosphereTags = [
  "Around the world with 100 artists",
  "What's your story",
  "Complete the story",
  "Impressionism",
  "Pointillism",
];

/** Display-only: internal tab keys stay activities | books | art-history | ways */
const MUSEUM_WINGS = [
  {
    key: 'activities',
    title: 'Daily Art Studio',
    helper: 'Artworks from daily creative prompts',
  },
  {
    key: 'books',
    title: 'Book Activity Gallery',
    helper: 'Artworks inspired by ARTmosphere books',
  },
  {
    key: 'art-history',
    title: 'Art History Gallery',
    helper: 'Creative responses to art history journeys',
  },
  {
    key: 'ways',
    title: 'Ways of Seeing Studio',
    helper: 'Reimagined artworks from ARTmosphere Studio',
  },
  {
    key: 'general',
    title: 'General Exhibit',
    helper: 'Public artworks shared beyond book activities',
    navigateTo: '/gallery/general',
  },
];

const SALON_UNLOCK_CTA =
  'salon-unlock-cta shrink-0 whitespace-nowrap rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] px-2.5 py-1 text-[10px] font-semibold leading-tight text-white shadow-[0_4px_12px_rgba(234,88,12,0.2)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(234,88,12,0.26)] sm:px-3 sm:py-1.5 sm:text-xs';

function GalleryStudioFiltersSync({
  artworks,
  artistProfiles,
  selectedTag,
  searchArtist,
  setFilteredArtworks,
  getArtistName,
}) {
  const { ageFilter } = useArtistSalonAgeFilter();

  useEffect(() => {
    let result = artworks;
    if (selectedTag !== 'All') {
      result = result.filter((art) => art.tag === selectedTag);
    }
    if (searchArtist.trim() !== '') {
      result = result.filter((art) =>
        getArtistName(art.uid).toLowerCase().includes(searchArtist.toLowerCase())
      );
    }
    if (ageFilter && ageFilter !== 'all') {
      result = result.filter((art) => {
        const profile = artistProfiles.find((p) => p.uid === art.uid);
        return artistMatchesAgeFilter(profile || {}, ageFilter);
      });
    }
    setFilteredArtworks(result);
  }, [
    ageFilter,
    selectedTag,
    searchArtist,
    artworks,
    artistProfiles,
    setFilteredArtworks,
    getArtistName,
  ]);

  return null;
}

function WaysAgeFilterNotice({ waysStyles, waysLoading }) {
  const { ageFilter, uidMatchesAgeFilter } = useArtistSalonAgeFilter();
  if (waysLoading || ageFilter === 'all' || !Object.keys(waysStyles || {}).length) return null;
  const hasMatch = Object.values(waysStyles).some((group) =>
    (group.items || []).some((item) => uidMatchesAgeFilter(item.uid || item.userId))
  );
  if (hasMatch) return null;
  return (
    <div className="col-span-full text-sm text-[#8A6A44]">
      No public submissions in this age range yet.
    </div>
  );
}

function WaysStylesSalonGrid({
  waysStyles,
  myWaysStyles,
  isAuthenticated,
  navigate,
  setSelectedWaysGroup,
  setShowWaysGallery,
  setShowWaysPicker,
}) {
  const { ageFilter, uidMatchesAgeFilter } = useArtistSalonAgeFilter();

  const visibleGroups = React.useMemo(
    () =>
      Object.values(waysStyles || {})
        .map((group) => ({
          ...group,
          items:
            ageFilter === 'all'
              ? group.items || []
              : (group.items || []).filter((item) =>
                  uidMatchesAgeFilter(item.uid || item.userId)
                ),
        }))
        .filter((group) => group.items.length > 0),
    [waysStyles, ageFilter, uidMatchesAgeFilter]
  );

  if (visibleGroups.length === 0) return null;

  return visibleGroups.map((group) => {
    const unlocked = myWaysStyles.has(group.styleId);
    const firstItem = group.items[0];
    let cover = firstItem?.imageUrl || firstItem?.transformedImageUrl || firstItem?.artworkUrl;

    if (cover) {
      cover = resolveImageUrl(cover);
    }

    return (
      <div
        key={group.styleId}
        className="overflow-hidden rounded-[28px] border border-[#F6D57B]/80 bg-gradient-to-b from-[#FFFDF9] to-[#FFF5E8] shadow-[0_14px_40px_rgba(120,72,20,0.1)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_48px_rgba(120,72,20,0.14)]"
      >
        <div className="relative h-32 w-full sm:h-40">
          {cover ? (
            <img
              src={cover}
              alt={group.styleName}
              className={`w-full h-full object-cover ${unlocked ? '' : 'filter blur-sm scale-[1.02]'}`}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl mb-2">🎨</div>
                <div className="text-xs font-medium text-amber-700">Artwork</div>
              </div>
            </div>
          )}
          <div className="absolute top-2 right-2 bg-white/80 text-amber-700 text-xs px-2 py-1 rounded-full">
            {group.items.length} shared
          </div>
          {!unlocked && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="px-3 py-1 rounded-full bg-black/50 text-white text-xs">Locked</div>
            </div>
          )}
        </div>
        <div className="p-3 text-left sm:p-4">
          <div className="flex items-start justify-between gap-2 sm:items-center sm:gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-base font-semibold text-[#2F251B] sm:text-lg">{group.styleName}</div>
              <div className="text-[11px] text-[#8A6A44] sm:text-sm">Ways of Seeing Studio</div>
            </div>
            <button
              type="button"
              onClick={() => {
                if (unlocked) {
                  setSelectedWaysGroup(group);
                  setShowWaysGallery(true);
                } else {
                  if (!isAuthenticated) {
                    navigate('/login');
                    return;
                  }
                  setShowWaysPicker(true);
                }
              }}
              className={SALON_UNLOCK_CTA}
            >
              {unlocked ? (
                <>
                  <span className="sm:hidden">Enter</span>
                  <span className="hidden sm:inline">Enter Gallery</span>
                </>
              ) : (
                <>
                  <span className="sm:hidden">Unlock</span>
                  <span className="hidden sm:inline">Contribute to Unlock</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  });
}

/** Salon navigation — Explore Salon + age dropdown on all screen sizes. */
function SalonWingNav({ activeTab, setActiveTab, navigate }) {
  const { ageFilter, setAgeFilter } = useArtistSalonAgeFilter();

  const handleWingSelect = (value) => {
    const wing = MUSEUM_WINGS.find((w) => w.key === value);
    if (!wing) return;
    if (wing.navigateTo) {
      navigate(wing.navigateTo);
      return;
    }
    setActiveTab(value);
  };

  const activeWing = MUSEUM_WINGS.find((w) => w.key === activeTab && !w.navigateTo);

  return (
    <div className="salon-nav-bar flex-shrink-0 border-b border-[#F5D99B]/40 px-3 py-2 sm:px-6 md:px-8">
      <div className="mx-auto grid max-w-3xl grid-cols-1 gap-2 sm:max-w-4xl sm:grid-cols-2 sm:items-end sm:gap-3 md:max-w-2xl lg:max-w-3xl">
        <div className="min-w-0">
          <label
            htmlFor="salon-explore-select"
            className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8A6A44]"
          >
            Explore Salon
          </label>
          <div className="relative">
            <select
              id="salon-explore-select"
              value={activeTab}
              onChange={(e) => handleWingSelect(e.target.value)}
              className={SALON_FILTER_SELECT_CLASS}
              aria-label="Explore Salon — choose a gallery wing"
            >
              {MUSEUM_WINGS.map(({ key, title }) => (
                <option key={key} value={key}>
                  {title}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A6A44]"
              strokeWidth={2}
              aria-hidden
            />
          </div>
        </div>
        <ArtistAgeFilterSelect
          value={ageFilter}
          onChange={setAgeFilter}
          id="salon-age-select"
          className="min-w-0 sm:max-w-none"
        />
      </div>
      {activeWing?.helper ? (
        <p className="mx-auto mt-1.5 max-w-3xl line-clamp-2 text-[10px] leading-snug text-[#8A6A44] sm:text-xs md:max-w-2xl lg:max-w-3xl">
          {activeWing.helper}
        </p>
      ) : null}
    </div>
  );
}

export default function GalleryStudio() {
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [artistProfiles, setArtistProfiles] = useState([]);
  const [selectedTag, setSelectedTag] = useState("All");
  const [searchArtist, setSearchArtist] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);
  const [activeTab, setActiveTab] = useState('activities'); // 'activities' | 'books' | 'art-history' | 'ways'
  const [showWaysPicker, setShowWaysPicker] = useState(false);
  const [showInlineUpload, setShowInlineUpload] = useState(false);
  const [inlineTitle, setInlineTitle] = useState('');
  const [inlineTechnique, setInlineTechnique] = useState('');
  const [inlineInspiration, setInlineInspiration] = useState('');
  const [inlineDescription, setInlineDescription] = useState('');
  const [inlineVisibility, setInlineVisibility] = useState(true);
  const [inlineIsBookActivity, setInlineIsBookActivity] = useState(false);
  const [inlineBookTag, setInlineBookTag] = useState('Around the world with 100 artists');
  const [inlineActivityCode, setInlineActivityCode] = useState('');
  const [inlineFile, setInlineFile] = useState(null);
  const [inlineUploading, setInlineUploading] = useState(false);
  const [myArtworks, setMyArtworks] = useState([]);
  const [waysLoading, setWaysLoading] = useState(false);
  const [waysStyles, setWaysStyles] = useState({}); // { styleId: { styleName, items: [...] } }
  const [myWaysStyles, setMyWaysStyles] = useState(new Set()); // Set of styleIds the user has shared in
  const [showWaysGallery, setShowWaysGallery] = useState(false);
  const [selectedWaysGroup, setSelectedWaysGroup] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSrc, setViewerSrc] = useState('');
  const [viewerItem, setViewerItem] = useState(null);
  const [viewerIsOwner, setViewerIsOwner] = useState(false);
  const [showUnshareConfirm, setShowUnshareConfirm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const artworkId = new URLSearchParams(window.location.search).get('artworkId');
  const tabParam = new URLSearchParams(window.location.search).get('tab');

  useEffect(() => {
    const urls = [
      ...(filteredArtworks || []).flatMap((art) => [art?.thumbnailUrl, art?.imageUrl, art?.artworkUrl]),
      ...(myArtworks || []).flatMap((art) => [art?.thumbnailUrl, art?.imageUrl, art?.artworkUrl]),
      ...Object.values(waysStyles || {}).flatMap((group) =>
        (group?.items || []).flatMap((item) => [item?.imageUrl, item?.artworkUrl, item?.transformedImageUrl, item?.originalImageUrl])
      )
    ].filter(Boolean);
    warmResolvedImageUrls(urls, 48).catch(() => {});
  }, [filteredArtworks, myArtworks, waysStyles]);

  // Check if activeTab is passed via navigation state or URL param
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      // Clear the state to prevent re-applying on re-render
      window.history.replaceState({}, document.title);
    } else if (tabParam && ['activities', 'books', 'art-history', 'ways'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.state, tabParam]);

  // Legacy Ways of Seeing viewer reactions (per‑tutorial reactions) have been
  // replaced by the unified ArtworkReactions component, which stores reactions
  // in the global reactions collection and powers notifications. The old
  // Firestore subcollection logic and local reaction state have been removed.

  const unshareViewerArtwork = async () => {
    try {
      if (!viewerItem?.id || !user?.uid || viewerItem?.userId !== user.uid) return;
      await setDoc(doc(db, 'tutorialArtworks', viewerItem.id), { visibility: 'private' }, { merge: true });
      // Optimistically remove from current style group
      if (selectedWaysGroup) {
        const updated = { ...selectedWaysGroup, items: selectedWaysGroup.items.filter(i => i.id !== viewerItem.id) };
        setSelectedWaysGroup(updated);
      }
      // Also update the main waysStyles map so the count reduces in the card
      if (selectedWaysGroup?.styleId && waysStyles[selectedWaysGroup.styleId]) {
        const copy = { ...waysStyles };
        copy[selectedWaysGroup.styleId] = {
          ...copy[selectedWaysGroup.styleId],
          items: (copy[selectedWaysGroup.styleId].items || []).filter(i => i.id !== viewerItem.id)
        };
        setWaysStyles(copy);
      }
      setViewerOpen(false);
      setShowUnshareConfirm(false);
    } catch (e) {
      console.warn('Failed to unshare:', e?.message);
    }
  };

  useEffect(() => {
    // Use real-time listener for shared/public content (immediate updates)
    const artworksRef = collection(db, "artworks");
    // Limit initial feed to the most recent 60 public artworks to avoid unbounded reads.
    const q = query(
      artworksRef,
      where("visibility", "==", "public"),
      orderBy("createdAt", "desc"),
      limit(60)
    );
    
    let unsubscribe = () => {}; // Default no-op function
    try {
      unsubscribe = onSnapshot(q, (querySnapshot) => {
        const filtered = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter(
            (art) =>
              art.tag &&
              artmosphereTags.includes(art.tag)
          );
        
        console.log('✅ [GALLERY] Real-time update received:', filtered.length, 'artworks');
        setArtworks(filtered);
        setFilteredArtworks(filtered);
      }, (error) => {
        console.error('Error in artworks listener:', error);
        // Fallback to basic query without orderBy if index error
        const fallbackQ = query(artworksRef, where("visibility", "==", "public"));
        try {
          onSnapshot(fallbackQ, (snapshot) => {
            const filtered = snapshot.docs
              .map((doc) => ({ id: doc.id, ...doc.data() }))
              .filter(
                (art) =>
                  art.tag &&
                  artmosphereTags.includes(art.tag)
              );
            setArtworks(filtered);
            setFilteredArtworks(filtered);
          }, undefined, { purpose: 'GalleryStudio-GeneralExhibit-fallback', component: 'GalleryStudio', essential: true });
        } catch (fallbackError) {
          console.error('[GALLERY] Error in fallback query:', fallbackError);
        }
      }, { purpose: 'GalleryStudio-GeneralExhibit', component: 'GalleryStudio', essential: true });
      
      // Ensure unsubscribe is a function
      if (!unsubscribe || typeof unsubscribe !== 'function') {
        console.warn('[GALLERY] onSnapshot did not return a function for General Exhibit');
        unsubscribe = () => {}; // No-op function
      }
    } catch (snapshotError) {
      console.error('[GALLERY] Error setting up General Exhibit snapshot:', snapshotError);
      unsubscribe = () => {}; // No-op function
    }
    
    // Cleanup listener on unmount - always return a function
    return () => {
      try {
        if (unsubscribe && typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch (err) {
        console.error('[GALLERY] Error unsubscribing General Exhibit listener:', err);
      }
    };
  }, []);

  useEffect(() => {
    const fetchProfiles = async () => {
      // Check cache first
      const cacheKey = createCacheKey('artistProfiles', { v: 2 });
      const cached = getCache(cacheKey);
      if (cached && cached.some?.((p) => 'birthday' in p)) {
        console.log('✅ [GALLERY] Using cached artist profiles:', cached.length);
        setArtistProfiles(cached);
        return;
      }
      
      const snapshot = await getDocs(collection(db, "artistProfiles"));
      const profilesWithConsent = await Promise.all(
        snapshot.docs.map(async (profileDoc) => {
          const profileData = profileDoc.data();
          const uid = profileData.uid;
          
          // Fetch consent settings to determine display name
          // Use backend API to bypass Firestore security rules
          // Default to showing (public) - only hide if explicitly set to false
          let displayName = profileData.name || profileData.nickname || 'Unknown Artist';
          try {
            const backendUrl = backendConfig.baseUrl;
            const response = await fetch(`${backendUrl}/api/consent/public-display/${uid}`);
            
            if (response.ok) {
              const data = await response.json();
              // Backend returns true by default, false only if explicitly set
              const canDisplayName = data.canDisplayName !== false; // Show unless explicitly false
              
              console.log(`🔍 GalleryStudio: ${uid} - canDisplayName: ${canDisplayName}`);
              
              if (canDisplayName) {
                // Use real name if name sharing is allowed (default or explicitly true)
                displayName = profileData.name || profileData.nickname || 'Unknown Artist';
              } else {
                // Use nickname if name sharing is explicitly disabled
                displayName = profileData.nickname || profileData.name || 'Unknown Artist';
                console.log(`🔍 GalleryStudio: Using nickname for ${uid}: ${displayName}`);
              }
            } else {
              console.warn(`🔍 GalleryStudio: Failed to fetch consent for ${uid}, defaulting to show (public)`);
              // Default to showing if API call fails
              displayName = profileData.name || profileData.nickname || 'Unknown Artist';
            }
          } catch (error) {
            console.warn('Error fetching consent for artist:', uid, error);
            // Default to showing if fetch fails
            displayName = profileData.name || profileData.nickname || 'Unknown Artist';
          }
          
          return {
            uid,
            birthday: profileData.birthday || '',
            name: displayName, // Use consent-aware display name
            originalName: profileData.name, // Keep original for reference
            nickname: profileData.nickname,
          };
        })
      );
      
      // Cache the results (10 min cache - profiles don't change often)
      setCache(cacheKey, profilesWithConsent, 10 * 60 * 1000);
      console.log('✅ [GALLERY] Artist profiles cached');
      
      setArtistProfiles(profilesWithConsent);
    };
    fetchProfiles();
  }, []);

  const getArtistName = (uid) => {
    const profile = artistProfiles.find((p) => p.uid === uid);
    return profile?.name || "Unknown Artist";
  };

  // Load user's artworks when Ways of Seeing tab is active
  useEffect(() => {
    const loadMine = async () => {
      if (!user) { setMyArtworks([]); return; }
      
      // Check cache first
      const cacheKey = createCacheKey('myArtworks', {}, user.uid);
      const cached = getCache(cacheKey);
      if (cached) {
        console.log('✅ [GALLERY] Using cached my artworks:', cached.length);
        setMyArtworks(cached);
        return;
      }
      
      try {
        setWaysLoading(true);
        // Fetch from both artworks and sharedArtworks (Artist Wall) collections
        const [artworksSnap, sharedArtworksSnap] = await Promise.all([
          getDocs(query(collection(db, 'artworks'), where('uid', '==', user.uid))),
          getDocs(query(collection(db, 'sharedArtworks'), where('uid', '==', user.uid)))
        ]);
        
        // Combine artworks from both collections
        const artworks = artworksSnap.docs.map(d => ({
          id: d.id,
          ...d.data(),
          imageUrl: d.data().imageUrl || d.data().artworkUrl,
          thumbnailUrl: d.data().thumbnailUrl || d.data().artworkThumbnailUrl || d.data().artworkThumbnail || d.data().imageUrl || d.data().artworkUrl,
          title: d.data().title || d.data().artworkTitle,
          source: 'artworks'
        }));
        
        const sharedArtworks = sharedArtworksSnap.docs.map(d => ({
          id: d.id,
          ...d.data(),
          imageUrl: d.data().artworkUrl || d.data().imageUrl,
          thumbnailUrl: d.data().thumbnailUrl || d.data().artworkThumbnailUrl || d.data().artworkThumbnail || d.data().artworkUrl || d.data().imageUrl,
          title: d.data().artworkTitle || d.data().title,
          source: 'sharedArtworks'
        }));
        
        // Combine and deduplicate by imageUrl (same artwork might be in both collections)
        const allArtworks = [...artworks, ...sharedArtworks];
        const uniqueArtworks = [];
        const seenUrls = new Set();
        
        for (const art of allArtworks) {
          const url = art.imageUrl;
          if (url && !seenUrls.has(url)) {
            seenUrls.add(url);
            uniqueArtworks.push(art);
          }
        }
        
        // Sort by creation date (newest first)
        const sorted = uniqueArtworks.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt?._seconds * 1000 || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt?._seconds * 1000 || 0;
          return bTime - aTime;
        });
        
        // Cache the results (3 min cache - user's own artworks)
        setCache(cacheKey, sorted, 3 * 60 * 1000);
        console.log('✅ [GALLERY] My artworks cached');
        
        console.log('📦 Loaded artworks for Ways of Seeing picker:', {
          fromArtworks: artworks.length,
          fromSharedArtworks: sharedArtworks.length,
          unique: sorted.length
        });
        
        setMyArtworks(sorted);
      } catch (error) {
        console.error('❌ Error loading artworks for Ways of Seeing:', error);
        setMyArtworks([]);
      } finally {
        setWaysLoading(false);
      }
    };
    const loadWaysOfSeeing = () => {
      if (activeTab !== 'ways') return () => {};
      
      // CRITICAL: Use closure variable to capture unsubscribe function
      // This ensures React always has a valid function to call during cleanup
      let unsubscribePublicFn = () => {
        console.log('[GALLERY] Default no-op cleanup for Ways of Seeing');
      };
      
      try {
        setWaysLoading(true);
        
        // Use real-time listener for public tutorial artworks (immediate updates)
        const pubQ = query(collection(db, 'tutorialArtworks'), where('visibility', '==', 'public'), orderBy('createdAt', 'desc'));
        
        try {
          const snapshotResult = onSnapshot(pubQ, (pubSnap) => {
            const grouped = {};
            pubSnap.docs.forEach(docu => {
              const d = docu.data();
              const sid = d.styleId || (d.styleName || '').toLowerCase().replace(/\s+/g, '');
              // Exclude any book-related items from Ways of Seeing
              const styleNameLc = (d.styleName || '').toLowerCase();
              if (styleNameLc.includes('book')) {
                return;
              }
              if (!grouped[sid]) grouped[sid] = { styleId: sid, styleName: d.styleName || 'Style', items: [], _seen: new Set() };
              const key = `${d.userId || d.uid || 'anon'}_${d.imageUrl || d.transformedImageUrl || ''}`;
              if (!grouped[sid]._seen.has(key)) {
                grouped[sid]._seen.add(key);
                // Ensure imageUrl is set - try multiple fields
                const itemData = {
                  id: docu.id,
                  ...d,
                  imageUrl: d.imageUrl || d.transformedImageUrl || d.artworkUrl || '',
                  originalArtworkUrl: d.originalArtworkUrl || d.originalImageUrl || '',
                  originalArtworkId: d.originalArtworkId || ''
                };
                // Log if imageUrl is missing for debugging
                if (!itemData.imageUrl) {
                  console.warn('⚠️ [GALLERY] Ways of Seeing item missing imageUrl:', {
                    id: docu.id,
                    styleId: d.styleId,
                    styleName: d.styleName,
                    hasTransformedImageUrl: !!d.transformedImageUrl,
                    hasArtworkUrl: !!d.artworkUrl,
                    allFields: Object.keys(d)
                  });
                }
                grouped[sid].items.push(itemData);
              }
            });
            // strip _seen helper before setting state
            const cleaned = Object.fromEntries(Object.entries(grouped).map(([k,v]) => [k, { styleId: v.styleId, styleName: v.styleName, items: v.items }]));
            
            console.log('✅ [GALLERY] Real-time update for Ways of Seeing:', Object.keys(cleaned).length, 'styles');
            setWaysStyles(cleaned);
            setWaysLoading(false);
          }, (error) => {
            console.error('Error in Ways of Seeing listener:', error);
            // Fallback to basic query without orderBy if index error
            if (error.code === 'failed-precondition') {
              console.log('⚠️ Index error, using fallback query without orderBy');
              const fallbackQ = query(collection(db, 'tutorialArtworks'), where('visibility', '==', 'public'));
              try {
                // CRITICAL: Capture the unsubscribe function from fallback query in closure
                const fallbackUnsubscribe = onSnapshot(fallbackQ, (snapshot) => {
                  const grouped = {};
                  snapshot.docs.forEach(docu => {
                    const d = docu.data();
                    const sid = d.styleId || (d.styleName || '').toLowerCase().replace(/\s+/g, '');
                    const styleNameLc = (d.styleName || '').toLowerCase();
                    if (styleNameLc.includes('book')) return;
                    if (!grouped[sid]) grouped[sid] = { styleId: sid, styleName: d.styleName || 'Style', items: [], _seen: new Set() };
                    const key = `${d.userId || d.uid || 'anon'}_${d.imageUrl || d.transformedImageUrl || ''}`;
                    if (!grouped[sid]._seen.has(key)) {
                      grouped[sid]._seen.add(key);
                      grouped[sid].items.push({ id: docu.id, ...d });
                    }
                  });
                  const cleaned = Object.fromEntries(Object.entries(grouped).map(([k,v]) => [k, { styleId: v.styleId, styleName: v.styleName, items: v.items }]));
                  setWaysStyles(cleaned);
                  setWaysLoading(false);
                }, undefined, { purpose: 'GalleryStudio-WaysOfSeeing-fallback', component: 'GalleryStudio', essential: true });
                // CRITICAL: Update closure variable with fallback unsubscribe function
                if (fallbackUnsubscribe && typeof fallbackUnsubscribe === 'function' && !(fallbackUnsubscribe instanceof Promise)) {
                  unsubscribePublicFn = fallbackUnsubscribe;
                  console.log('[GALLERY] ✅ Fallback unsubscribe function captured');
                } else {
                  console.warn('[GALLERY] Fallback query did not return a valid function');
                  unsubscribePublicFn = () => {};
                }
              } catch (fallbackError) {
                console.error('Error in fallback query:', fallbackError);
                unsubscribePublicFn = () => {}; // Ensure we have a no-op function
                setWaysStyles({});
                setWaysLoading(false);
              }
            } else {
              unsubscribePublicFn = () => {}; // Ensure we have a no-op function
              setWaysStyles({});
              setWaysLoading(false);
            }
          }, { purpose: 'GalleryStudio-WaysOfSeeing-public', component: 'GalleryStudio', essential: true });
          
          // CRITICAL: Capture the unsubscribe function from onSnapshot in closure
          if (snapshotResult && typeof snapshotResult === 'function' && !(snapshotResult instanceof Promise)) {
            unsubscribePublicFn = snapshotResult;
            console.log('[GALLERY] ✅ Primary unsubscribe function captured');
          } else {
            console.warn('[GALLERY] onSnapshot did not return a valid function for Ways of Seeing');
            unsubscribePublicFn = () => {}; // No-op function
          }
        } catch (snapshotError) {
          console.error('[GALLERY] Error setting up Ways of Seeing snapshot:', snapshotError);
          unsubscribePublicFn = () => {}; // No-op function
          setWaysStyles({});
          setWaysLoading(false);
        }
      } catch (outerError) {
        console.error('[GALLERY] Outer error in Ways of Seeing setup:', outerError);
        unsubscribePublicFn = () => {}; // No-op function
        setWaysStyles({});
        setWaysLoading(false);
      }
        
        // User participation listener (if user is logged in)
        // CRITICAL: Use closure variable for user unsubscribe function
        let unsubscribeUserFn = () => {
          console.log('[GALLERY] Default no-op cleanup for user Ways of Seeing');
        };
        
        if (user) {
          try {
            const minePubQ = query(collection(db, 'tutorialArtworks'), where('userId', '==', user.uid));
            const uaQ = query(collection(db, 'userActivities'), where('userId', '==', user.uid), where('type', '==', 'tutorial'));
            
            const unsubscribeMine = onSnapshot(minePubQ, (minePubSnap) => {
              const mineSet = new Set();
              minePubSnap.docs.forEach(docu => {
                const d = docu.data();
                const sid = d.styleId || (d.styleName || '').toLowerCase().replace(/\s+/g, '');
                if (sid) mineSet.add(sid);
              });
              
              // Also check userActivities
              getDocs(uaQ).then(uaSnap => {
                uaSnap.docs.forEach(docu => {
                  const d = docu.data();
                  const sid = d?.styleInfo?.id || (d?.styleInfo?.name || '').toLowerCase().replace(/\s+/g, '');
                  if (sid) mineSet.add(sid);
                });
                setMyWaysStyles(mineSet);
              }).catch(err => {
                console.error('[GALLERY] Error loading user activities for Ways of Seeing:', err);
              });
            }, undefined, { purpose: 'GalleryStudio-WaysOfSeeing-mine', component: 'GalleryStudio', essential: true });
            
            // CRITICAL: Capture unsubscribe function in closure variable
            if (unsubscribeMine && typeof unsubscribeMine === 'function' && !(unsubscribeMine instanceof Promise)) {
              unsubscribeUserFn = unsubscribeMine;
              console.log('[GALLERY] ✅ User unsubscribe function captured');
            } else {
              console.warn('[GALLERY] onSnapshot did not return a valid function for user Ways of Seeing');
              unsubscribeUserFn = () => {}; // No-op function
            }
          } catch (userSnapshotError) {
            console.error('[GALLERY] Error setting up user Ways of Seeing snapshot:', userSnapshotError);
            unsubscribeUserFn = () => {}; // No-op function
          }
        }
        
        // CRITICAL: Capture closure variables for cleanup
        const capturedUnsubscribePublic = unsubscribePublicFn;
        const capturedUnsubscribeUser = unsubscribeUserFn;
        
        // Return cleanup function (ensure they're functions before calling)
        return () => {
          console.log('[GALLERY] Cleaning up Ways of Seeing listeners');
          try {
            if (capturedUnsubscribePublic && typeof capturedUnsubscribePublic === 'function' && !(capturedUnsubscribePublic instanceof Promise)) {
              capturedUnsubscribePublic();
              console.log('[GALLERY] ✅ Public listener unsubscribed');
            } else {
              console.warn('[GALLERY] Invalid public unsubscribe type:', typeof capturedUnsubscribePublic, capturedUnsubscribePublic instanceof Promise);
            }
          } catch (err) {
            console.error('[GALLERY] Error unsubscribing Ways of Seeing public listener:', err);
          }
          try {
            if (capturedUnsubscribeUser && typeof capturedUnsubscribeUser === 'function' && !(capturedUnsubscribeUser instanceof Promise)) {
              capturedUnsubscribeUser();
              console.log('[GALLERY] ✅ User listener unsubscribed');
            } else {
              console.warn('[GALLERY] Invalid user unsubscribe type:', typeof capturedUnsubscribeUser, capturedUnsubscribeUser instanceof Promise);
            }
          } catch (err) {
            console.error('[GALLERY] Error unsubscribing Ways of Seeing user listener:', err);
          }
        };
    };
    // CRITICAL: Always return a cleanup function, even when activeTab !== 'ways'
    // This prevents React from calling undefined cleanup from previous renders
    let cleanup = () => {}; // Default no-op function
    
    if (activeTab === 'ways') { 
      loadMine(); 
      try {
        const result = loadWaysOfSeeing();
        // CRITICAL: Validate result is a function, not a Promise
        if (result && typeof result === 'function' && !(result instanceof Promise)) {
          cleanup = result;
        } else {
          console.error('[GALLERY] loadWaysOfSeeing returned invalid type:', typeof result, result instanceof Promise);
          cleanup = () => {}; // Fallback to no-op
        }
      } catch (error) {
        console.error('[GALLERY] Error loading Ways of Seeing:', error);
        cleanup = () => {}; // Fallback to no-op
      }
    }
    
    // CRITICAL: Capture cleanup in closure to prevent stale references
    const capturedCleanup = cleanup;
    
    // Cleanup listeners on unmount or tab change - ALWAYS return a function
    const cleanupFn = () => {
      try {
        if (capturedCleanup && typeof capturedCleanup === 'function' && !(capturedCleanup instanceof Promise)) {
          capturedCleanup();
        } else {
          console.error('[GALLERY] Invalid cleanup type:', typeof capturedCleanup, capturedCleanup instanceof Promise);
        }
      } catch (err) {
        console.error('[GALLERY] Error cleaning up Ways of Seeing:', err);
      }
    };
    
    // CRITICAL: Validate cleanup function before returning
    if (typeof cleanupFn !== 'function' || cleanupFn instanceof Promise) {
      console.error('[GALLERY] ❌ CRITICAL: cleanupFn is not a function!');
      return () => {}; // Emergency fallback
    }
    
    return cleanupFn;
  }, [activeTab, user]);

  // Scroll to specific artwork if artworkId is provided in URL
  useEffect(() => {
    if (artworkId && filteredArtworks.length > 0) {
      const targetArtwork = filteredArtworks.find(art => art.id === artworkId);
      if (targetArtwork) {
        // Find the artwork element and scroll to it
        const artworkElement = document.getElementById(`artwork-${artworkId}`);
        if (artworkElement) {
          setTimeout(() => {
            artworkElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
            // Add a highlight effect
            artworkElement.classList.add('ring-4', 'ring-amber-400', 'ring-opacity-75');
            setTimeout(() => {
              artworkElement.classList.remove('ring-4', 'ring-amber-400', 'ring-opacity-75');
            }, 3000);
          }, 500);
        }
      }
    }
  }, [artworkId, filteredArtworks]);

  const museumCtaPrimary =
    'shrink-0 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] px-3 py-1 text-[11px] font-semibold text-white shadow-[0_4px_14px_rgba(234,88,12,0.2)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(234,88,12,0.26)] sm:px-4 sm:py-1.5 sm:text-xs';

  return (
    <ArtistSalonAgeFilterProvider profiles={artistProfiles}>
    <GalleryStudioFiltersSync
      artworks={artworks}
      artistProfiles={artistProfiles}
      selectedTag={selectedTag}
      searchArtist={searchArtist}
      setFilteredArtworks={setFilteredArtworks}
      getArtistName={getArtistName}
    />
    <div
      className="artm-page artm-page-pad relative flex min-h-[100dvh] md:h-screen flex-col overflow-hidden px-0 md:px-8 lg:px-12"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, rgba(255, 221, 132, 0.22), transparent 35%), radial-gradient(circle at 100% 100%, rgba(255, 183, 77, 0.16), transparent 40%), linear-gradient(180deg, #FFFDF7 0%, #FFF7E8 100%)',
      }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -left-[8%] top-0 h-[42%] w-[55%] rounded-b-[50%] border border-[#F5D99B]/40 bg-gradient-to-b from-white/50 to-transparent opacity-80"
          aria-hidden
        />
        <div
          className="absolute left-1/2 top-[6%] h-24 w-40 -translate-x-1/2 rounded-t-full border border-[#E8C97A]/50 bg-gradient-to-b from-[#FFF9EF]/90 to-transparent shadow-[0_12px_40px_rgba(120,72,20,0.06)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute right-[6%] top-[18%] h-28 w-36 rotate-3 rounded-lg border-2 border-[#F5D99B]/60 bg-gradient-to-br from-white/40 to-amber-50/20 shadow-inner"
          aria-hidden
        />
      </div>
      <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden opacity-[0.07]">
        <SmartImage
          src={splashImage}
          alt=""
          className="absolute -right-8 top-24 max-w-[min(420px,45vw)] rotate-12"
          style={{ mixBlendMode: 'multiply', filter: 'blur(1px)' }}
          loading="eager"
          fetchPriority="low"
        />
      </div>
      <KidBackground variant="aurora" intensity={0.38} className="z-0" />

      <div className="relative z-10 mx-auto flex min-h-0 w-full max-w-[1400px] flex-1 flex-col py-2 md:py-4 lg:py-5">
        <style>{`
          @keyframes museumFadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes museumWingIn {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-none border-0 border-[#F6D57B]/90 shadow-[0_24px_70px_rgba(120,72,20,0.12)] md:rounded-[36px] md:border md:border-[#F6D57B]/80"
          style={{
            background: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            animation: 'museumFadeIn 0.55s ease-out forwards',
          }}
        >
          <header className="relative z-30 flex-shrink-0 border-b border-[#F5D99B]/50 bg-[#FFFDF9]/80 px-4 py-2.5 backdrop-blur-md sm:px-6 md:px-8">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,214,140,0.28),transparent_70%)]" aria-hidden />
            <div className="relative flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 max-w-2xl pr-2">
                <h1 className="font-serif text-base font-semibold leading-tight tracking-tight text-[#2F251B] sm:text-xl md:text-2xl">
                  ARTmosphere Artist Salon
                </h1>
                <p className="mt-0.5 text-[10px] font-medium italic leading-snug text-[#8A6A44] sm:text-[13px] line-clamp-2">
                  A creative gathering space for artists.
                </p>
                <p className="mt-0.5 hidden text-[10px] leading-snug text-[#6B4E32] sm:block sm:text-[13px] line-clamp-2">
                  Step into a gallery of young artists, creative activities, and imaginative discoveries.
                </p>
              </div>
              <div className="artm-toolbar artm-toolbar-scroll flex-shrink-0 w-full sm:w-auto sm:justify-end">
                <button type="button" onClick={() => navigate('/my-art-world')} className={pillNavBtn} title="Artist Corner" aria-label="Artist Corner">
                  <LayoutDashboard className="h-3.5 w-3.5 shrink-0 text-[#8A6A44]" strokeWidth={1.75} />
                  <span className="hidden sm:inline">Artist Corner</span>
                </button>
                <button type="button" onClick={() => navigate('/home')} className={pillNavBtn} title="Home" aria-label="Home">
                  <Home className="h-3.5 w-3.5 shrink-0 text-[#8A6A44]" strokeWidth={1.75} />
                  <span className="hidden sm:inline">Home</span>
                </button>
                {!isAuthenticated && (
                  <>
                    <button type="button" onClick={() => navigate('/login')} className={pillNavBtn} title="Login">
                      <User className="h-3.5 w-3.5 shrink-0 text-[#8A6A44]" strokeWidth={1.75} />
                      <span>Login</span>
                    </button>
                    <button type="button" onClick={() => navigate('/signup')} className={joinNavBtn} title="Join Community">
                      <span className="hidden sm:inline">Join Community</span>
                      <span className="sm:hidden">Join</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </header>

          <SalonWingNav activeTab={activeTab} setActiveTab={setActiveTab} navigate={navigate} />

          <div className="flex-shrink-0 px-3 py-1.5 sm:px-6 md:px-8">
            <div
              className="mx-auto flex max-w-4xl flex-col gap-2 rounded-xl border border-[#F6D57B]/70 bg-gradient-to-r from-[#FFFDF8] to-[#FFF7ED] px-3 py-2 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-4 sm:py-2"
            >
              {activeTab === 'activities' && (
                <>
                  <h2 className="text-left text-xs font-semibold leading-snug text-[#2F251B] sm:text-sm md:max-w-[55%]">
                    Today&apos;s children&apos;s gallery
                  </h2>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/login');
                        return;
                      }
                      navigate('/daily-art-activity');
                    }}
                    className={museumCtaPrimary}
                  >
                    Visit Today&apos;s Activity
                  </button>
                </>
              )}
              {activeTab === 'books' && (
                <>
                  <h2 className="text-left text-xs font-semibold leading-snug text-[#2F251B] sm:text-sm md:max-w-[55%]">
                    Book-inspired shared galleries
                  </h2>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/login');
                        return;
                      }
                      navigate('/create-books');
                    }}
                    className={museumCtaPrimary}
                  >
                    Visit Artmosphere Collections
                  </button>
                </>
              )}
              {activeTab === 'art-history' && (
                <>
                  <h2 className="text-left text-xs font-semibold leading-snug text-[#2F251B] sm:text-sm md:max-w-[58%]">
                    Art history co-learning wall
                  </h2>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/login');
                        return;
                      }
                      navigate('/learn-art-history');
                    }}
                    className={museumCtaPrimary}
                  >
                    Explore Art History
                  </button>
                </>
              )}
              {activeTab === 'ways' && (
                <>
                  <h2 className="text-left text-xs font-semibold leading-snug text-[#2F251B] sm:text-sm md:max-w-[55%]">
                    Ways of Seeing shared wall
                  </h2>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/login');
                        return;
                      }
                      setShowWaysPicker(true);
                    }}
                    className={museumCtaPrimary}
                  >
                    Open studio
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Keep tab content mounted for instant premium switching */}
            <div className={`${activeTab === 'activities' ? 'block' : 'hidden'} mx-auto max-w-7xl px-4 pb-8 pt-1 text-center text-[#2F251B]/85`}>
              <DailyActivitiesShowcase />
            </div>

            <div className={`${activeTab === 'books' ? 'block' : 'hidden'} mx-auto max-w-7xl px-4 pb-8 pt-1 text-center text-[#2F251B]/85`}>
              <BookLearningSpace />
            </div>

            <div className={`${activeTab === 'art-history' ? 'block' : 'hidden'} mx-auto max-w-7xl px-4 pb-8 pt-1 text-center text-[#2F251B]/85`}>
              <ArtHistoryCoLearningSpace key={`art-history-${user?.uid || 'anonymous'}`} />
            </div>

            <div className={`${activeTab === 'ways' ? 'block' : 'hidden'} mx-auto max-w-7xl px-4 pb-8 pt-1 text-center text-[#2F251B]/85`}>
              {/* Ways of Seeing grid — scrolls with main content */}
              <div className="px-4">
                {/* Styles Grid (public showcase with participation gating) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-4">
                  {waysLoading && (
                    <div className="col-span-full text-sm text-[#8A6A44]">Loading Ways of Seeing Studio…</div>
                  )}
                  {!waysLoading && Object.keys(waysStyles).length === 0 && (
                    <div className="col-span-full text-sm text-[#8A6A44]">No public submissions yet. Be the first to share!</div>
                  )}
                  <WaysAgeFilterNotice waysStyles={waysStyles} waysLoading={waysLoading} />
                  <WaysStylesSalonGrid
                    waysStyles={waysStyles}
                    myWaysStyles={myWaysStyles}
                    isAuthenticated={isAuthenticated}
                    navigate={navigate}
                    setSelectedWaysGroup={setSelectedWaysGroup}
                    setShowWaysGallery={setShowWaysGallery}
                    setShowWaysPicker={setShowWaysPicker}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showWaysPicker && (
        <div {...coLearningGalleryPickerBackdropProps} onClick={() => setShowWaysPicker(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#e8e0d4]">
              <div className={coLearningInkHeading}>Choose artwork for Ways of Seeing</div>
              <button onClick={() => setShowWaysPicker(false)} className="text-[#141210] hover:text-black text-xl leading-none rounded-full px-2 py-1 hover:bg-stone-100">×</button>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className={`text-sm ${coLearningInkBody}`}>Use an existing artwork or upload a new one.</div>
                <button
                  onClick={() => setShowInlineUpload(true)}
                  className="art-btn-primary px-4 py-2 rounded-full font-semibold transition"
                >
                  Upload New Artwork
                </button>
              </div>
              {waysLoading ? (
                <div className={`py-16 text-center ${coLearningInkMuted}`}>Loading your artworks...</div>
              ) : myArtworks.length === 0 ? (
                <div className={`py-12 text-center ${coLearningInkBody}`}>No artworks yet. Please upload a new one.</div>
              ) : (
                <div className="relative">
                  {/* Horizontal Scroller Container */}
                  <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-amber-300 scrollbar-track-amber-100" style={{ scrollbarWidth: 'thin' }}>
                    {myArtworks.map(art => (
                      <button
                        key={art.id}
                        onClick={() => {
                          setShowWaysPicker(false);
                          navigate('/art-world/selection', { 
                            state: { 
                              artwork: { 
                                imageUrl: art.imageUrl, 
                                title: art.title || art.artworkTitle, 
                                technique: art.technique, 
                                inspiration: art.inspiration 
                              }, 
                              fromPage: 'ways-picker' 
                            } 
                          });
                        }}
                        className="group flex-shrink-0 transform overflow-hidden rounded-xl border-2 border-[#e8e0d4] shadow-sm transition-all hover:scale-105 hover:border-[#cfc7bc] hover:shadow-lg"
                        style={{ minWidth: '200px', width: '200px' }}
                      >
                        <div className="w-full h-40 bg-gray-100 relative">
                          <SmartImage
                            src={art.thumbnailUrl || art.imageUrl || art.artworkUrl}
                            alt={art.title || art.artworkTitle || 'Artwork'}
                            className="w-full h-full object-cover"
                            containerClassName="w-full h-full"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50"><div class="text-center"><div class="text-3xl mb-1">🎨</div><div class="text-xs font-medium text-amber-700">Artwork</div></div></div>';
                            }}
                          />
                        </div>
                        <div className="p-3 text-left bg-white">
                          <div className={`text-sm font-semibold truncate mb-1 ${coLearningInkTitle}`}>
                            {art.title || art.artworkTitle || 'Untitled'}
                          </div>
                          {art.technique && (
                            <div className={`text-xs truncate ${coLearningInkMuted}`}>{art.technique}</div>
                          )}
                          {!art.technique && art.description && (
                            <div className={`text-xs truncate ${coLearningInkMuted}`}>{art.description.substring(0, 30)}...</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {/* Scroll indicator */}
                  {myArtworks.length > 3 && (
                    <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none flex items-center justify-end pr-2">
                      <div className="text-amber-400 text-xs">→</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedPost && (
        <div {...coLearningGalleryPickerBackdropProps} onClick={() => setSelectedPost(null)}>
          <div className="bg-white/95 backdrop-blur-xl border border-[#e8e0d4] rounded-2xl shadow-xl w-full max-w-5xl mx-auto flex flex-col md:flex-row overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPost.imageUrl}
              alt={selectedPost.title}
              className="md:w-2/3 w-full h-[80vh] object-cover"
            />
            <div className="md:w-1/3 w-full p-6 overflow-y-auto">
              <button
                onClick={() => setSelectedPost(null)}
                className="absolute top-2 right-4 text-[#141210] text-2xl hover:bg-stone-100 rounded-full w-10 h-10 leading-none"
              >
                &times;
              </button>
              <h2 className={`text-xl mb-2 ${coLearningInkTitle}`}>
                {selectedPost.title}
              </h2>
              <p className={`text-sm mb-2 ${coLearningInkBody}`}>
                <strong className="text-[#141210]">Description:</strong> {selectedPost.description}
              </p>
              <p className={`text-sm mb-1 ${coLearningInkBody}`}>
                <strong className="text-[#141210]">Technique:</strong> {selectedPost.technique}
              </p>
              <p className={`text-sm mb-1 ${coLearningInkBody}`}>
                <strong className="text-[#141210]">Inspiration:</strong> {selectedPost.inspiration}
              </p>
              <p className={`text-sm mb-1 ${coLearningInkBody}`}>
                <strong className="text-[#141210]">Collection:</strong> {selectedPost.tag}
              </p>
              <p className={`text-sm mt-2 ${coLearningInkBody}`}>
                <button
                  onClick={() => {
                    setSelectedPost(null);
                    navigate(`/artist-public-gallery/${selectedPost.uid}`);
                  }}
                  className={coLearningInkLink}
                >
                  Artist: {getArtistName(selectedPost.uid)}
                </button>
              </p>
              
              {/* Artwork Reactions in Modal */}
              <div className="mt-4 pt-4 border-t border-[#e8e0d4]">
                <ArtworkReactions 
                  artworkId={selectedPost.id} 
                  artistId={selectedPost.uid}
                  onKudosChange={(count) => console.log('Kudos count updated:', count)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inline upload modal for Ways of Seeing */}
      {showInlineUpload && (
        <div {...coLearningGalleryUploadBackdropProps} onClick={() => setShowInlineUpload(false)}>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#e8e0d4]">
              <div className={coLearningInkHeading}>Upload New Artwork</div>
              <button onClick={() => setShowInlineUpload(false)} className="text-[#141210] hover:bg-stone-100 rounded-full px-2 text-xl leading-none">×</button>
            </div>
            <div className="p-4 space-y-5 overflow-y-auto">
              {/* 1. Artwork Type */}
              <div className="rounded-2xl border border-[#e8e0d4] bg-[#faf8f5] p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#141210] text-xs font-bold text-white">1</div>
                  <div className="flex-1">
                    <div className={`mb-2 text-lg font-semibold ${coLearningInkTitle}`}>Artwork Type</div>
                    <label className="flex cursor-pointer items-center gap-3">
                      <input type="checkbox" checked={inlineIsBookActivity} onChange={(e)=>{ setInlineIsBookActivity(e.target.checked); if(!e.target.checked){ setInlineActivityCode(''); } }} className="h-5 w-5 rounded border-2 border-[#cfc7bc] text-[#141210] focus:ring-2 focus:ring-stone-400" />
                      <span className={`font-medium ${coLearningInkBody}`}>Is this artwork from an Artmosphere book activity?</span>
                    </label>
                    {inlineIsBookActivity && (
                      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className={`mb-2 block text-sm font-semibold ${coLearningInkBody}`}>Select Artmosphere Book *</label>
                          <select value={inlineBookTag} onChange={(e)=>setInlineBookTag(e.target.value)} className="w-full rounded-xl border-2 border-[#e0d8cc] bg-white p-3 shadow-sm focus:border-stone-400 focus:ring-2 focus:ring-stone-300">
                            {artmosphereTags.map(t => (<option key={t} value={t}>{t}</option>))}
                          </select>
                        </div>
                        <div>
                          <label className={`mb-2 block text-sm font-semibold ${coLearningInkBody}`}>Activity Code *</label>
                          <input value={inlineActivityCode} onChange={(e)=>setInlineActivityCode(e.target.value)} className="w-full rounded-xl border-2 border-[#e0d8cc] bg-white p-3 shadow-sm focus:border-stone-400 focus:ring-2 focus:ring-stone-300" placeholder="Enter the activity code from the book" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* 2. Artwork Details */}
              <div className="rounded-2xl border border-[#e8e0d4] bg-[#faf8f5] p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#141210] text-xs font-bold text-white">2</div>
                  <div className={`text-lg font-semibold ${coLearningInkTitle}`}>Artwork Details</div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={`mb-2 block text-sm font-semibold ${coLearningInkBody}`}>Title *</label>
                    <input value={inlineTitle} onChange={(e)=>setInlineTitle(e.target.value)} className="w-full rounded-xl border-2 border-[#e0d8cc] bg-white p-3 shadow-sm focus:border-stone-400 focus:ring-2 focus:ring-stone-300" placeholder="Enter artwork title" required />
                  </div>
                  <div>
                    <label className={`mb-2 block text-sm font-semibold ${coLearningInkBody}`}>Technique</label>
                    <input value={inlineTechnique} onChange={(e)=>setInlineTechnique(e.target.value)} className="w-full rounded-xl border-2 border-[#e0d8cc] bg-white p-3 shadow-sm focus:border-stone-400 focus:ring-2 focus:ring-stone-300" placeholder="e.g., Watercolor, Digital Art, Collage" />
                  </div>
                  <div>
                    <label className={`mb-2 block text-sm font-semibold ${coLearningInkBody}`}>Inspiration</label>
                    <input value={inlineInspiration} onChange={(e)=>setInlineInspiration(e.target.value)} className="w-full rounded-xl border-2 border-[#e0d8cc] bg-white p-3 shadow-sm focus:border-stone-400 focus:ring-2 focus:ring-stone-300" placeholder="What inspired this artwork?" />
                  </div>
                  <div>
                    <label className={`mb-2 block text-sm font-semibold ${coLearningInkBody}`}>Description *</label>
                    <textarea value={inlineDescription} onChange={(e)=>setInlineDescription(e.target.value)} rows={3} className="w-full resize-none rounded-xl border-2 border-[#e0d8cc] bg-white p-3 shadow-sm focus:border-stone-400 focus:ring-2 focus:ring-stone-300" placeholder="Describe your artwork and creative process" required />
                  </div>
                </div>
              </div>
              {/* 3. Image */}
              <div className="rounded-2xl border border-[#e8e0d4] bg-[#faf8f5] p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#141210] text-xs font-bold text-white">3</div>
                  <div className={`text-lg font-semibold ${coLearningInkTitle}`}>Upload Image</div>
                </div>
                <input type="file" accept="image/*" onChange={(e)=>setInlineFile(e.target.files[0])} />
              </div>
              {/* 4. Visibility */}
              <div className="rounded-2xl border border-[#e8e0d4] bg-[#faf8f5] p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#141210] text-xs font-bold text-white">4</div>
                  <div className={`text-lg font-semibold ${coLearningInkTitle}`}>Visibility Settings</div>
                </div>
                <label className="flex cursor-pointer items-center gap-3">
                  <input type="checkbox" checked={inlineVisibility} onChange={(e)=>setInlineVisibility(e.target.checked)} className="h-5 w-5 rounded border-2 border-[#cfc7bc] text-[#141210] focus:ring-2 focus:ring-stone-400" />
                  <span className={`font-medium ${coLearningInkBody}`}>Make this artwork public</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={()=>setShowInlineUpload(false)} className={`rounded-full border border-[#e0d8cc] bg-white px-4 py-2 hover:bg-stone-50 ${coLearningInkBody}`}>Cancel</button>
                <button disabled={inlineUploading || !inlineFile || !inlineTitle || !inlineDescription} onClick={async()=>{
                  try{
                    if(!user){ navigate('/login'); return; }
                    setInlineUploading(true);
                    const fd=new FormData();
                    fd.append('artwork', inlineFile);
                    const res=await fetch(`${require('../config/backendConfig').API_ENDPOINTS.backendBase}/api/upload-artwork`,{method:'POST',body:fd});
                    if(!res.ok){ throw new Error('upload failed'); }
                    const j=await res.json();
                    const imagePath=j?.imageUrl||'';
                    const full=`${require('../config/backendConfig').API_ENDPOINTS.backendBase}${imagePath}`;
                    console.log('🎨 Inline upload response:', j);
                    console.log('🎨 Image path:', imagePath);
                    console.log('🎨 Full URL:', full);
                    // Save to artworks collection mirroring Artist Wall fields
                    await addDoc(collection(db,'artworks'),{
                      uid: user.uid,
                      title: inlineTitle,
                      technique: inlineTechnique,
                      inspiration: inlineInspiration,
                      description: inlineDescription,
                      imageUrl: full,
                      tag: inlineIsBookActivity ? inlineBookTag : (artmosphereTags[0] || ''),
                      activityCode: inlineIsBookActivity ? inlineActivityCode : '',
                      visibility: inlineVisibility ? 'public' : 'private',
                      createdAt: serverTimestamp()
                    });
                    setShowInlineUpload(false);
                    setInlineUploading(false);
                    navigate('/art-world/selection',{ state:{ artwork:{ imageUrl: full, title: inlineTitle, technique: inlineTechnique, inspiration: inlineInspiration }, fromPage:'ways-inline-upload' } });
                  }catch(e){ console.warn('Inline upload failed',e); setInlineUploading(false); }
                }} className="px-5 py-2 rounded-full text-white art-btn-primary">{inlineUploading?'Uploading...':'Upload & Continue'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ways of Seeing Gallery Modal for a specific style */}
      {showWaysGallery && selectedWaysGroup && (
        <div {...coLearningGallerySheetBackdropProps} onClick={() => { setShowWaysGallery(false); setSelectedWaysGroup(null); }}>
          <div
            className="flex h-[min(98dvh,calc(100dvh-0.5rem))] w-full max-w-[min(1760px,calc(100vw-0.75rem))] flex-col overflow-hidden rounded-xl border border-[#e8e0d4] bg-white shadow-2xl md:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Compact header — activity dashboard style */}
            <div className="max-h-[72px] flex-shrink-0 border-b border-[#e8e0d4]/80 bg-white/95 shadow-sm backdrop-blur-sm md:max-h-[80px]">
              <div className="flex h-full min-h-[52px] items-center justify-between px-3 py-2 md:px-6 md:py-2.5">
                <h3 className={`truncate pr-2 text-sm md:text-base ${coLearningInkHeading}`}>{selectedWaysGroup.styleName}</h3>
                <button onClick={() => { setShowWaysGallery(false); setSelectedWaysGroup(null); }} className="flex-shrink-0 rounded-full bg-stone-100/90 p-1.5 text-[#141210] transition-colors hover:bg-stone-200/90 md:p-2" aria-label="Close">×</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 md:p-6">
              {/* User's own artwork - side by side comparison */}
              {user && (() => {
                const myItem = selectedWaysGroup.items.find(item => item.userId === user.uid);
                if (myItem) {
                  const originalUrl = myItem.originalArtworkUrl || myItem.originalImageUrl || '';
                  const transformedUrl = myItem.imageUrl || myItem.transformedImageUrl || '';
                  const artworkTitle = myItem.title || 'Untitled Artwork';
                  return (
                    <div className="mb-8">
                      <div className={`mb-4 text-xl font-semibold ${coLearningInkTitle}`}>Your Transformation</div>
                      <div className={`mb-2 text-base font-medium ${coLearningInkBody}`}>{artworkTitle}</div>
                      <div className="grid grid-cols-1 gap-4 rounded-xl border border-[#e8e0d4] bg-[#faf8f5] p-3 md:grid-cols-2 md:gap-6 md:p-6">
                        <div className="text-center">
                          <div className={`mb-2 text-sm font-semibold md:mb-3 md:text-base ${coLearningInkBody}`}>Original Artwork</div>
                          <div className="h-56 w-full overflow-hidden rounded-lg border border-[#e0d8cc] bg-gray-100 md:h-[min(72vh,720px)]">
                            {originalUrl ? (
                              <SmartImage src={originalUrl} alt="Original" className="h-full w-full object-contain" containerClassName="h-full w-full" />
                            ) : (
                              <div className={`flex h-full w-full items-center justify-center ${coLearningInkMuted}`}>Original not available</div>
                            )}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className={`mb-2 text-sm font-semibold md:mb-3 md:text-base ${coLearningInkBody}`}>{selectedWaysGroup.styleName} Style</div>
                          <div className="h-56 w-full overflow-hidden rounded-lg border border-[#e0d8cc] bg-gray-100 md:h-[min(72vh,720px)]">
                            {transformedUrl ? (
                              <SmartImage src={transformedUrl} alt="Transformed" className="h-full w-full cursor-pointer object-contain transition hover:opacity-90" containerClassName="h-full w-full" onClick={() => { setViewerSrc(transformedUrl); setViewerItem(myItem); setViewerOpen(true); }} />
                            ) : (
                              <div className={`flex h-full w-full items-center justify-center ${coLearningInkMuted}`}>Transformed not available</div>
                            )}
                          </div>
                          {myItem.id && (
                            <div className="mt-3 flex items-center justify-center gap-2 md:gap-3 flex-wrap">
                              <ArtworkReactions
                                artworkId={myItem.id}
                                artistId={myItem.userId}
                                enableComments={false}
                              />
                              {/* Owner unshare button */}
                              {myItem.userId === user?.uid && (
                                <button
                                  onClick={() => {
                                    setViewerItem(myItem);
                                    setShowUnshareConfirm(true);
                                  }}
                                  className="rounded-lg border border-[#e0d8cc] bg-white px-2 py-1 text-xs text-[#141210] hover:bg-stone-50 md:px-3 md:py-1"
                                >
                                  Unshare
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Other users' artworks - horizontal scroller with side-by-side */}
              {(() => {
                const otherItems = selectedWaysGroup.items.filter(item => !user || item.userId !== user.uid);
                if (otherItems.length > 0) {
                  return (
                    <div>
                      <div className={`mb-3 text-lg font-semibold md:mb-4 md:text-xl ${coLearningInkTitle}`}>See How Others Transformed Their Artworks ({otherItems.length})</div>
                      <div className="relative">
                        <div 
                          id="waysScroller" 
                          className="w-full overflow-x-auto pb-4" 
                          style={{ 
                            scrollbarWidth: 'auto', 
                            scrollbarColor: '#78716c #e7e5e4',
                            WebkitOverflowScrolling: 'touch'
                          }}
                        >
                          <style>{`
                            #waysScroller::-webkit-scrollbar {
                              height: 8px;
                            }
                            #waysScroller::-webkit-scrollbar-track {
                              background: #e7e5e4;
                              border-radius: 4px;
                            }
                            #waysScroller::-webkit-scrollbar-thumb {
                              background: #78716c;
                              border-radius: 4px;
                            }
                            #waysScroller::-webkit-scrollbar-thumb:hover {
                              background: #57534e;
                            }
                          `}</style>
                          <div className="flex gap-3 md:gap-6 px-2 md:px-4">
                            {otherItems.map(item => {
                              // Get URLs - try multiple fields and resolve them to absolute URLs
                              let originalUrl = item.originalArtworkUrl || item.originalImageUrl || '';
                              let transformedUrl = item.imageUrl || item.transformedImageUrl || item.artworkUrl || '';
                              
                              // Resolve URLs to absolute URLs
                              if (originalUrl) {
                                originalUrl = resolveImageUrl(originalUrl);
                              }
                              if (transformedUrl) {
                                transformedUrl = resolveImageUrl(transformedUrl);
                              }
                              
                              // For other users, show artist name + style name instead of "My [Style] Creation"
                              const artistName = item.userId ? getArtistName(item.userId) : 'Artist';
                              const styleName = selectedWaysGroup.styleName || 'Artwork';
                              const artworkTitle = item.title && !item.title.toLowerCase().includes('my ') 
                                ? item.title 
                                : `${artistName}'s ${styleName} Creation`;
                              return (
                                <div key={item.id} className="min-w-[320px] max-w-[320px] flex-shrink-0 overflow-hidden rounded-xl border border-[#e8e0d4] bg-white shadow-lg md:min-w-[min(520px,92vw)] md:max-w-[min(520px,92vw)]">
                                  <div className="border-b border-[#e8e0d4] bg-[#faf8f5] p-2 md:p-3">
                                    <div className={`truncate text-xs font-semibold md:text-sm ${coLearningInkTitle}`} title={artworkTitle}>{artworkTitle}</div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 p-2 md:gap-3 md:p-3">
                                    <div className="text-center">
                                      <div className={`mb-1 text-xs font-semibold ${coLearningInkBody}`}>Original</div>
                                      <div className="relative h-52 w-full overflow-hidden rounded border border-[#e0d8cc] bg-gray-100 md:h-72">
                                        {originalUrl ? (
                                          <SmartImage
                                            src={originalUrl}
                                            alt="Original"
                                            className="w-full h-full object-contain"
                                            containerClassName="w-full h-full"
                                            onError={(e) => {
                                              e.currentTarget.style.display = 'none';
                                              const parent = e.currentTarget.parentElement;
                                              if (parent && !parent.querySelector('.image-error-placeholder')) {
                                                const placeholder = document.createElement('div');
                                                placeholder.className = 'image-error-placeholder w-full h-full flex items-center justify-center text-stone-500 text-xs';
                                                placeholder.textContent = 'Original';
                                                parent.appendChild(placeholder);
                                              }
                                            }}
                                          />
                                        ) : (
                                          <div className={`flex h-full w-full items-center justify-center text-xs ${coLearningInkMuted}`}>Original</div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-center">
                                      <div className={`mb-1 text-xs font-semibold ${coLearningInkBody}`}>{selectedWaysGroup.styleName}</div>
                                      <div className="relative h-52 w-full cursor-pointer overflow-hidden rounded border border-[#e0d8cc] bg-gray-100 transition hover:opacity-90 md:h-72" onClick={() => { if (transformedUrl) { setViewerSrc(transformedUrl); setViewerItem(item); setViewerOpen(true); } }}>
                                        {transformedUrl ? (
                                          <SmartImage
                                            src={transformedUrl}
                                            alt="Transformed"
                                            className="w-full h-full object-contain"
                                            containerClassName="w-full h-full"
                                            onError={(e) => {
                                              e.currentTarget.style.display = 'none';
                                              const parent = e.currentTarget.parentElement;
                                              if (parent && !parent.querySelector('.image-error-placeholder')) {
                                                const placeholder = document.createElement('div');
                                                placeholder.className = 'image-error-placeholder w-full h-full flex items-center justify-center text-stone-500 text-xs';
                                                placeholder.textContent = 'Transformed';
                                                parent.appendChild(placeholder);
                                              }
                                            }}
                                          />
                                        ) : (
                                          <div className={`flex h-full w-full items-center justify-center text-xs ${coLearningInkMuted}`}>Transformed</div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="border-t border-[#e8e0d4] bg-[#faf8f5] p-2 md:p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      {item.userId && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); navigate(`/artist-public-gallery/${item.userId}`); }}
                                          className={`min-w-0 max-w-full truncate text-xs font-semibold hover:underline md:text-sm ${coLearningInkLink}`}
                                          title="Open artist profile"
                                        >
                                          {getArtistName(item.userId)}
                                        </button>
                                      )}
                                      {item.id && (
                                        <div className="flex items-center flex-shrink-0">
                                          <ArtworkReactions
                                            artworkId={item.id}
                                            artistId={item.userId}
                                            enableComments={false}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        {otherItems.length > 1 && (
                          <>
                            <button
                              onClick={() => { const el = document.getElementById('waysScroller'); if (el) el.scrollBy({ left: -600, behavior: 'smooth' }); }}
                              className="absolute left-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#e0d8cc] bg-white/95 text-sm text-[#141210] shadow-md transition-all hover:bg-white hover:shadow-lg md:h-10 md:w-10 md:text-base"
                              aria-label="Previous"
                            >
                              ←
                            </button>
                            <button
                              onClick={() => { const el = document.getElementById('waysScroller'); if (el) el.scrollBy({ left: 600, behavior: 'smooth' }); }}
                              className="absolute right-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#e0d8cc] bg-white/95 text-sm text-[#141210] shadow-md transition-all hover:bg-white hover:shadow-lg md:h-10 md:w-10 md:text-base"
                              aria-label="Next"
                            >
                              →
                            </button>
                          </>
                        )}
                      </div>
                      {otherItems.length > 1 && (
                        <div className={`mt-4 text-center text-sm font-medium ${coLearningInkBody}`}>Scroll horizontally or use the arrow buttons to see all {otherItems.length} transformations.</div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen viewer for Ways of Seeing images */}
      {viewerOpen && (
        <div {...coLearningExpandedGalleryViewerBackdropProps} onClick={() => setViewerOpen(false)}>
          <button 
            type="button"
            className="absolute right-2 top-2 z-[10000] flex h-10 w-10 items-center justify-center rounded-full text-[#141210] shadow-[0_8px_28px_rgba(0,0,0,0.18)] transition-transform hover:scale-105 md:right-8 md:top-8 md:h-12 md:w-12" 
            onClick={(e) => { e.stopPropagation(); setViewerOpen(false); }}
            style={{
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}
            aria-label="Close fullscreen artwork"
          >
            <svg className="h-5 w-5 md:h-6 md:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="relative max-w-[min(96vw,1400px)] px-2" onClick={(e) => e.stopPropagation()}>
            <SmartImage src={viewerSrc} alt="Artwork" className="max-h-[min(90vh,920px)] max-w-[95vw] rounded-xl border border-white/30 bg-white object-contain shadow-2xl drop-shadow-2xl md:rounded-2xl md:border-2" containerClassName="max-h-[min(90vh,920px)] max-w-[95vw]" loading="eager" fetchPriority="high" />
            <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-[#e8e0d4] bg-white/95 p-2 shadow backdrop-blur-sm md:mt-3 md:rounded-xl md:p-3">
              <div className={`text-xs md:text-sm ${coLearningInkBody}`}>
                {viewerItem?.userId ? (
                    <button
                      type="button"
                      className={`font-semibold ${coLearningInkLink}`}
                      onClick={() => { setViewerOpen(false); navigate(`/artist-public-gallery/${viewerItem.userId}`); }}
                    >
                    {getArtistName(viewerItem.userId)}
                  </button>
                ) : (
                  <span className="font-medium text-[#141210]">Shared artwork</span>
                )}
              </div>
              {viewerItem?.id && (
                <div className="flex items-center gap-2 md:gap-3">
                    <ArtworkReactions
                      artworkId={viewerItem.id}
                      artistId={viewerItem.userId}
                      enableComments={false}
                    />
                  {viewerItem?.userId && user?.uid && viewerItem.userId === user.uid && (
                      <button
                        type="button"
                        onClick={() => setShowUnshareConfirm(true)}
                        className="rounded-full border border-[#e0d8cc] bg-white px-3 py-1 text-xs text-[#141210] hover:bg-stone-50"
                      >
                      Unshare
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unshare confirm modal */}
      {showUnshareConfirm && (
        <div {...coLearningGalleryUnshareBackdropProps} onClick={() => setShowUnshareConfirm(false)}>
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-[#e8e0d4] bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-stone-200 to-stone-300 text-sm font-bold text-[#141210] shadow">A</div>
              <h3 className={`mb-2 text-xl ${coLearningInkTitle}`}>Unshare from Co‑Learning?</h3>
              <p className={`mb-4 text-sm ${coLearningInkBody}`}>Unsharing will lock this style again for you and you may not see new works from other artists. We encourage staying public to promote co‑learning, but the choice is yours.</p>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowUnshareConfirm(false)} className={`rounded-full border border-[#e0d8cc] bg-white px-4 py-2 hover:bg-stone-50 ${coLearningInkBody}`}>Keep Public</button>
                <button onClick={unshareViewerArtwork} className="px-4 py-2 rounded-full text-white art-btn-primary">Unshare</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </ArtistSalonAgeFilterProvider>
  );
}
